import { icon } from '@jupiterone/jupiter-web-toolkit';
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit/src/theme/types';
import FilterIcon from '@jupiterone/react-brand-icons/jupiterone/filter';
import {
  Divider,
  Fade,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { X } from 'react-feather';
import { GraphFilters, GraphVertex } from '../types';
import FilterPanelCheckbox from './FilterPanelCheckbox';
import FilterPanelHeader from './FilterPanelHeader';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    filterBox: {
      borderRadius: 0,
      position: 'absolute',
      top: theme.spacing(10),
      left: theme.spacing(3),
      width: '200px',
      maxHeight: 'calc(100% - 72px)',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
        width: 0,
        height: 0,
        background: 'transparent',
      },
    },
    header: {
      backgroundColor: theme.palette.primary.dark,
      borderTop: `solid 4px ${theme.palette.primary.dark}`,
      padding: theme.spacing(0, 0, 0.25, 0.75),
      position: 'sticky',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: theme.palette.common.white,
      fontWeight: 400,
      fontSize: 18,
      top: 0,
      zIndex: 1,
      '& button': {
        padding: theme.spacing(1),
      },
    },
    headerTextGroup: {
      alignItems: 'center',
      display: 'flex',
      paddingLeft: 0,
      '& button': {
        padding: '4px 5px 4px 3px',
      },
      '& h6': {
        color: theme.palette.common.white,
        fontWeight: 400,
        fontSize: 18,
      },
    },
    headerIcon: {
      color: theme.palette.common.white,
      height: 20,
      strokeWidth: 1.75,
      width: 20,
    },
    content: {
      maxHeight: 'calc(100vh - 250px)',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(0, 1),
    },
    filterList: {
      margin: theme.spacing(-0.5, 0, 1.5),
      '&:empty': {
        margin: theme.spacing(0),
      },
    },
    divider: {
      margin: theme.spacing(0, -1),
      '&:last-child': {
        visibility: 'hidden',
      },
    },
  })
);

interface Props {
  vertices: GraphVertex[];
  filters: GraphFilters;
  open: boolean;
  onClose: VoidFunction;
  onChange: (filters: GraphFilters) => void;
}

interface PanelState {
  values: string[];
  getIcon: () => any;
  open: boolean;
}

interface PropertyPanelState {
  open: boolean;
}

interface PanelStates {
  accounts: PanelState;
  types: PanelState;
  properties: PropertyPanelState;
}

const DEFAULT_PANEL_STATE = {
  open: false,
  values: [] as string[],
  getIcon: () => ({}),
};

const FilterPanel = ({
  open = true,
  vertices,
  filters,
  onClose,
  onChange,
}: Props) => {
  const classes = useStyles();
  const [panelStates, setPanelStates] = useState<PanelStates>({
    accounts: { ...DEFAULT_PANEL_STATE },
    types: { ...DEFAULT_PANEL_STATE },
    properties: { open: false },
  });
  const [classesByType, setClassesByType] = useState<{
    [type: string]: string;
  }>({});

  useEffect(() => {
    // Update the list of available types/accounts
    const newState = { ...panelStates };
    const types: string[] = [];
    const accounts: string[] = [];
    const newClassesByType: { [type: string]: string } = {};

    vertices.forEach((v) => {
      const type = v?.entity?._type?.[0];
      const className = v?.entity?._class?.[0];
      const account = v?.properties?.['tag.AccountName'] || 'unknown';

      if (type && types.indexOf(type) === -1) {
        types.push(type);
        if (className) {
          newClassesByType[type] = className;
        }
      }

      if (accounts.indexOf(account) === -1) {
        accounts.push(account);
      }
    });

    newState.types.values = types.sort();
    newState.accounts.values = accounts.sort();
    setPanelStates(newState);
    setClassesByType(newClassesByType);
  }, [vertices]);

  const toggleValue = (
    filterName: 'hiddenAccounts' | 'hiddenTypes',
    value: string
  ) => {
    const newFilters = { ...filters };
    const currentFilterValues: string[] = newFilters[filterName];
    newFilters[filterName] =
      currentFilterValues.indexOf(value) >= 0
        ? currentFilterValues.filter((v) => v !== value)
        : [...currentFilterValues, value];
    onChange(newFilters);
  };

  const toggleProperty = (propertyName: string) => {
    const newProperties = {
      ...filters.properties,
    };
    delete newProperties[propertyName];
    onChange({ ...filters, properties: newProperties });
  };

  const togglePanel = (panelName: keyof PanelStates) =>
    setPanelStates({
      ...panelStates,
      [panelName]: {
        ...panelStates[panelName],
        open: !panelStates[panelName].open,
      },
    });

  return (
    <Fade in={open}>
      <Paper className={classes.filterBox}>
        <div className={classes.header}>
          <div className={classes.headerTextGroup}>
            <IconButton onClick={onClose}>
              <FilterIcon className={classes.headerIcon} />
            </IconButton>
            <Typography variant={'h6'}>Filters</Typography>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className={classes.content}>
          <FilterPanelHeader
            open={panelStates.accounts.open}
            onClick={() => togglePanel('accounts')}
            title={'Account'}
          />
          <div className={classes.filterList}>
            {panelStates.accounts.open &&
              panelStates.accounts.values.map((account: string) => (
                <FilterPanelCheckbox
                  key={account}
                  checked={filters.hiddenAccounts.indexOf(account) === -1}
                  onSelect={() => toggleValue('hiddenAccounts', account)}
                  labelText={account}
                />
              ))}
          </div>
          <Divider className={classes.divider} />
          <FilterPanelHeader
            open={panelStates.types.open}
            onClick={() => togglePanel('types')}
            title={'Type'}
          />
          <div className={classes.filterList}>
            {panelStates.types.open &&
              panelStates.types.values.map((type: string) => {
                const iconPath = icon.entityToPath({
                  _type: type,
                  _class: classesByType[type],
                });
                const loadedIcon = {
                  type: icon.get(iconPath),
                };
                return (
                  <FilterPanelCheckbox
                    key={type}
                    checked={filters.hiddenTypes.indexOf(type) === -1}
                    onSelect={() => toggleValue('hiddenTypes', type)}
                    labelText={type}
                    LoadedIcon={loadedIcon.type}
                  />
                );
              })}
          </div>
          <Divider className={classes.divider} />
          {Object.keys(filters.properties).length > 0 && (
            <>
              <FilterPanelHeader
                open={panelStates.properties.open}
                onClick={() => togglePanel('properties')}
                title={'Properties'}
              />
              <div className={classes.filterList}>
                {Object.keys(filters.properties).map((propertyName: string) => {
                  const value = filters.properties[propertyName];
                  return (
                    <FilterPanelCheckbox
                      key={propertyName}
                      checked={true}
                      onSelect={() => toggleProperty(propertyName)}
                      labelText={`${propertyName} == ${value}`}
                    />
                  );
                })}
              </div>
              <Divider className={classes.divider} />
            </>
          )}
        </div>
      </Paper>
    </Fade>
  );
};
export default FilterPanel;
